import React, { useState, useEffect, useRef } from "react"
import ItemsList from "../components/ItemsList"
import "../styles/winbox.scss"
import "../styles/mainTerminalWindow.scss"
import DarkModeToggle from "./DarkModeToggle"
import {
  COMMAND_HELP,
  COMMANDS,
  QUICK_COMMANDS,
} from "../utils/LsDisplay.utils"

const LsDisplay = ({ text }) => {
  const isBrowser = typeof window !== "undefined"
  const [command, setCommand] = useState("ls -la")
  const [output, setOutput] = useState([])
  const [history, setHistory] = useState([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [showDropdown, setShowDropdown] = useState(false)
  const [displayMode, setDisplayMode] = useState("items") // "items", "help", "error", "none"
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)
  const [filters, setFilters] = useState({
    sections: [],
    titleFilter: "",
    tag: "",
    sort: "",
    limit: null,
    search: "",
  })

  const processCommand = command => {
    let cmd = command;
    if (!cmd.trim() || cmd.trim() === "ls -la") {
      setFilters({
        sections: [],
        tag: "",
        sort: "",
        limit: null,
        search: "",
      })
      setDisplayMode("items")
      setOutput([])

      if (cmd.trim() !== "" && (!history.length || history[history.length - 1] !== command)) {
        const newHistory = [...history, command].slice(-5)
        setHistory(newHistory)
        setHistoryIndex(-1)
      }
      return
    } else {
      cmd = cmd.trim().replace(/^ls -la\s*/, "")
    }

    if (cmd === "help") {
      const result = [
        "\nAvailable Commands:",
        "================\n",
        "Basic Commands:",
        "--------------",
        ...COMMAND_HELP.basics.map(
          ({ cmd, desc }) => `${cmd.padEnd(20)} - ${desc}`
        ),
        "\nFlags and Options:",
        "----------------",
        ...COMMAND_HELP.flags.map(
          ({ cmd, desc, usage }) =>
            `${cmd.padEnd(20)} - ${desc}\n${" ".repeat(22)}Usage: ${usage}`
        ),
        "\nExamples:",
        "---------",
        ...COMMAND_HELP.examples.map(
          ({ cmd, desc }) => `${cmd}\n${" ".repeat(22)}${desc}`
        ),
      ]
      setDisplayMode("help")
      setOutput(result)

      if (!history.length || history[history.length - 1] !== command) {
        const newHistory = [...history, command].slice(-5)
        setHistory(newHistory)
        setHistoryIndex(-1)
      }
      return
    }

    if (cmd === "clear") {
      setOutput([])
      setHistory([command])
      setHistoryIndex(-1)
      setDisplayMode("none")
      setFilters({
        sections: [],
        tag: "",
        sort: "",
        limit: null,
        search: "",
      })
      return
    }

    const possibleSections = ["blogs", "projects", "info"]
    let sections = []
    let commandParts = cmd.split(/\s+/)

    if (!possibleSections.includes(commandParts[0]) && !commandParts[0].startsWith("--")) {
      setOutput([
        `zsh: command not found: ${cmd}`,
      ])
      setDisplayMode("error")
      return
    }

    if (
      possibleSections.includes(commandParts[0]) ||
      commandParts[0].includes("|")
    ) {
      sections = commandParts[0].split("|").map(s => s.trim())
      if (!sections.every(s => possibleSections.includes(s))) {
        setOutput([
          `zsh: no such section: ${sections.find(
            s => !possibleSections.includes(s)
          )}`,
        ])
        setDisplayMode("error")
        return
      }
      commandParts.shift()
    }

    if (commandParts.includes("--top")) {
      if (
        sections.length === 0 ||
        (sections.length === 1 && sections[0] === "info")
      ) {
        sections = ["blogs", "projects"]
      } else {
        sections = sections.filter(s => ["blogs", "projects"].includes(s))
        if (sections.length === 0) {
          sections = ["blogs", "projects"]
        }
      }
    }

    let newFilters = {
      sections: sections,
      tag: "",
      sort: "",
      limit: null,
      search: "",
    }

    for (let i = 0; i < commandParts.length; i++) {
      const flag = commandParts[i]

      if (flag === "--top") {
        newFilters.sort = "-rank"
        newFilters.limit = 5
      } else if (flag === "--tag" && commandParts[i + 1]) {
        let tags = []
        i++
        while (i < commandParts.length && !commandParts[i].startsWith("--")) {
          tags.push(commandParts[i])
          i++
        }
        i--
        newFilters.tag = tags.join(" ")
      } else if (flag === "--search") {
        let searchTerms = []
        i++
        while (i < commandParts.length && !commandParts[i].startsWith("--")) {
          // Remove quotes if present
          let term = commandParts[i].replace(/^"|"$/g, '').replace(/^'|'$/g, '')
          searchTerms.push(term)
          i++
        }
        i-- // Back up one since we'll increment in the loop
        newFilters.search = searchTerms
      } else if (flag === "--sort" && commandParts[i + 1]) {
        const sortVal = commandParts[++i]
        if (
          !["time", "-time", "rank", "-rank", "name", "-name"].includes(sortVal)
        ) {
          setOutput([
            `zsh: invalid sort value: ${sortVal}. Use: time, rank, name (prefix with - for descending)`,
          ])
          setDisplayMode("error")
          return
        }
        newFilters.sort = sortVal
      } else if (flag === "--limit" && commandParts[i + 1]) {
        const limit = parseInt(commandParts[++i])
        if (isNaN(limit) || limit <= 0) {
          setOutput([`zsh: invalid limit value: must be a positive number`])
          setDisplayMode("error")
          return
        }
        newFilters.limit = limit
      } else if (flag.startsWith("--")) {
        setOutput([`zsh: unknown flag: ${flag}`])
        setDisplayMode("error")
        return
      }
    }

    // Add to history if not duplicate
    if (!history.length || history[history.length - 1] !== command) {
      const newHistory = [...history, command].slice(-5)
      setHistory(newHistory)
      setHistoryIndex(-1)
    }

    setFilters(newFilters)
    setDisplayMode("items")
    setOutput([])
  }

  const handleKeyDown = e => {
    if (e.key === "Enter") {
      processCommand(command)
      setCommand("")
      setShowDropdown(false)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      if (historyIndex < history.length - 1) {
        setHistoryIndex(prev => prev + 1)
        setCommand(history[history.length - 1 - historyIndex - 1])
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault()
      if (historyIndex > 0) {
        setHistoryIndex(prev => prev - 1)
        setCommand(history[history.length - 1 - historyIndex + 1])
      } else {
        setHistoryIndex(-1)
        setCommand("")
      }
    }
  }
  const handleDropdownClick = cmd => {
    setCommand(cmd)
    setShowDropdown(false)
    inputRef.current.focus();
  }

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div className="LsDisplayWindow">
      <div className="LsDisplayTaskbar">
        <span>CMD</span>
        <div style={{ position: "absolute", right: "10px", bottom: "-2px" }}>
          <DarkModeToggle />
        </div>
      </div>
      <div className="LsDisplayContent">
        <div className="terminal-area">
          <div className="terminal-output">
            {history.length > 0 && (
              <div className="history-display">
                {history.slice(-5).map((cmd, i) => (
                  <div 
                    key={i} 
                    className="history-line"
                    onClick={() => {
                      setCommand(cmd.trim())
                      inputRef.current.focus()
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="history-prompt">
                      {text}
                    </span>
                    {cmd.trim()}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="command-line">
            <span
              className="prompt"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              {text}
            </span>
            <input
              ref={inputRef}
              type="text"
              value={command}
              onChange={e => setCommand(e.target.value)}
              onKeyDown={handleKeyDown}
              className="command-input"
              spellCheck="false"
              autoFocus
            />
            {showDropdown && (
              <div className="command-dropdown" ref={dropdownRef}>
                <div className="quick-commands">
                  {QUICK_COMMANDS.map((cmd, i) => (
                    <div
                      key={i}
                      className="dropdown-item"
                      onClick={() => handleDropdownClick(cmd)}
                    >
                      {cmd}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="help-tooltip">
              <span className="help-icon">?</span>
              <div className="tooltip-content">
                <h4>Available Commands:</h4>
                {Object.entries(COMMANDS).map(([cmd, desc], i) => (
                  <div key={i} className="command-help">
                    <code>{cmd}</code>
                    <span>{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {output.map((line, i) => (
            <div key={i} className="output-line">
              {line}
            </div>
          ))}
        </div>
      </div>
      {isBrowser && displayMode === "items" && <ItemsList filters={filters} />}
    </div>
  )
}

export default LsDisplay
